import { TimelineTrack, TimelineItem } from '../../types';
import { DragInfoState } from '../../stores/use-timeline-store';
import { calculateMagneticInsertionPreview, pushItemsDuringResize } from '../../utils/gap-utils';

const MIN_ITEM_DURATION = 0.1;
const DURATION_TOLERANCE = 0.05;

export interface ValidationResult {
  isValid: boolean;
  reason?: string;
  magneticStart?: number;
  magneticPreview?: Array<{ id: string; start: number; end: number; duration: number }>;
}

/**
 * Validates if a drop position is valid for the given constraints
 */
export const validateDropPosition = (
  targetStart: number,
  targetDuration: number,
  targetTrackIndex: number,
  tracks: TimelineTrack[],
  excludeIds: string[],
  getDragInfo: () => DragInfoState | null,
  action?: "move" | "resize-start" | "resize-end"
): ValidationResult => {
  const targetEnd = targetStart + targetDuration;

  if (targetStart < 0) {
    return { isValid: false, reason: "Cannot place item before timeline start" };
  }

  if (targetTrackIndex < 0 || targetTrackIndex >= tracks.length) {
    return { isValid: false, reason: "Invalid track" };
  }

  // Check source duration constraints for video/audio items during resize operations
  if (action === "resize-start" || action === "resize-end") {
    const dragInfo = getDragInfo();
    if (dragInfo && dragInfo.selectedItemsSnapshot.length > 0) {
      const primaryItem = dragInfo.selectedItemsSnapshot.find(item => item.id === dragInfo.id);
      if (primaryItem && (primaryItem.type === 'video' || primaryItem.type === 'audio') && primaryItem.mediaSrcDuration !== undefined) {
        let calculatedMediaStart = primaryItem.mediaStart || 0;

        // For resize-start operations, adjust mediaStart based on position change
        if (action === "resize-start") {
          const startTimeDelta = targetStart - primaryItem.originalStart;
          calculatedMediaStart = Math.max(0, calculatedMediaStart + startTimeDelta);

          // Check if we're trying to resize before the beginning of media content
          if (startTimeDelta < 0 && (primaryItem.mediaStart || 0) + startTimeDelta < 0) {
            return {
              isValid: false,
              reason: `Cannot resize before the beginning of media content`
            };
          }
        }

        const maxAllowedDuration = primaryItem.mediaSrcDuration - calculatedMediaStart;

        // Use tolerance-based comparison to handle floating point precision issues
        if (targetDuration > maxAllowedDuration + DURATION_TOLERANCE) {
          return {
            isValid: false,
            reason: `Cannot resize beyond source duration (${maxAllowedDuration.toFixed(1)}s available)`
          };
        }
      }
    }
  }

  const targetTrack = tracks[targetTrackIndex];

  // For magnetic tracks, check if multiple items are being dragged
  if (targetTrack.magnetic) {
    const dragInfo = getDragInfo();
    const isMultiDrag = dragInfo && dragInfo.selectedItemsSnapshot.length > 1;

    // Prevent multiple items from being dropped onto magnetic tracks
    if (isMultiDrag) {
      return {
        isValid: false,
        reason: "Cannot drop multiple items onto magnetic tracks"
      };
    }

    // Only apply magnetic insertion logic for move operations
    if (action === "move") {
      const availableItems = targetTrack.items.filter(item => !excludeIds.includes(item.id));
      const preview = calculateMagneticInsertionPreview(
        availableItems,
        targetDuration,
        targetStart
      );

      return {
        isValid: true,
        magneticStart: preview.insertionStart,
        magneticPreview: preview.previewItems
      };
    }

    // For resize on magnetic tracks, allow without altering start via insertion logic
    return { isValid: true };
  }

  // For non-magnetic tracks, handle differently based on action
  if (action === "resize-start" || action === "resize-end") {
    // For resize operations, calculate what's actually achievable and create a pushing preview
    const resizedItemId = excludeIds[0]; // The first excluded ID is the item being resized
    if (resizedItemId) {
      const result = pushItemsDuringResize(
        targetTrack.items,
        resizedItemId,
        targetStart,
        targetStart + targetDuration
      );

      // Use the actual achievable position for validation
      return {
        isValid: true,
        magneticStart: result.actualStart, // Return the constrained start position
        magneticPreview: result.items.map((item: TimelineItem) => ({
          id: item.id,
          start: item.start,
          end: item.end,
          duration: item.end - item.start
        }))
      };
    }
  } else {
    // For move operations, check for overlaps as before
    const overlappingItems = targetTrack.items.filter(
      (item) =>
        !excludeIds.includes(item.id) &&
        targetStart < item.end &&
        targetEnd > item.start
    );

    if (overlappingItems.length > 0) {
      return { isValid: false, reason: "Overlaps with existing item" };
    }
  }

  return { isValid: true };
};

/**
 * Validates source duration constraints for resize operations
 */
export const validateSourceDuration = (
  newStart: number,
  newDuration: number,
  originalEnd: number,
  primaryItem: {
    type: string;
    mediaSrcDuration?: number;
    mediaStart?: number;
    originalStart: number;
  },
  action: "resize-start" | "resize-end"
): { start: number; duration: number } => {
  if ((primaryItem.type === 'video' || primaryItem.type === 'audio') && primaryItem.mediaSrcDuration !== undefined) {
    if (action === "resize-start") {
      const startTimeDelta = newStart - primaryItem.originalStart;
      const calculatedMediaStart = Math.max(0, (primaryItem.mediaStart || 0) + startTimeDelta);

      // Prevent resizing before the beginning of media content
      if (startTimeDelta < 0 && calculatedMediaStart <= 0) {
        const maxEarlierDelta = -(primaryItem.mediaStart || 0);
        newStart = primaryItem.originalStart + maxEarlierDelta;
        newDuration = originalEnd - newStart;
      }

      const maxAllowedDuration = primaryItem.mediaSrcDuration - calculatedMediaStart;

      if (newDuration > maxAllowedDuration) {
        newDuration = Math.max(MIN_ITEM_DURATION, maxAllowedDuration);
        newStart = originalEnd - newDuration;
      }
    } else if (action === "resize-end") {
      const mediaStartOffset = primaryItem.mediaStart || 0;
      const maxAllowedDuration = primaryItem.mediaSrcDuration - mediaStartOffset;

      if (newDuration > maxAllowedDuration) {
        newDuration = Math.max(MIN_ITEM_DURATION, maxAllowedDuration);
      }
    }
  }

  return { start: newStart, duration: newDuration };
};
